import React, { useContext, useState, useMemo } from 'react';
import { Box, Chip, Typography, Button, Tooltip, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ExpandLess, ExpandMore, InfoRounded } from '@mui/icons-material';
import useFoodGroups from '../../lib/hooks/useFoodGroups';
import { getWorkflowToken } from '../../lib/helpers/helpers';
import { UserContext } from '../../lib/contexts/UserContext';
import servingExamples from './servingExamples.json';

const useStyles = makeStyles((theme) => ({
  list: {
    ...theme.globalClasses.list,
    color: theme.palette.text.primary,
    padding: 0,
    margin: 0,
  },
  frame: {
    ...theme.globalClasses.frame,
    padding: '16px',
    width: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  foodGroupRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
    width: '100%',
  },
  valuesColumn: {
    width: '80px',
    minWidth: '80px',
    paddingLeft: '12px',
    display: 'flex',
    justifyContent: 'flex-start',
  },
  chipColumn: {
    width: '83px',
    minWidth: '83px',
    display: 'flex',
    justifyContent: 'center',
    padding: '0 12px',
  },
  infoIconColumn: {
    width: '24px',
    minWidth: '24px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  viewMoreButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '16px',
  },
}));

/**
 * Get serving examples tooltip content for a food group
 * @param {string} key - The food group key (e.g., "meta-vegetables")
 * @param {string} label - The display name of the food group
 * @returns {string|JSX.Element} - The tooltip content
 */
const getServingTooltipContent = (key, label) => {
  const examples = servingExamples[key] || [];

  if (examples.length === 0) {
    return `Current servings vs Goal servings for ${label}`;
  }

  return (
    <Box>
      <Typography
        sx={(theme) => ({
          color: theme.palette.toolTip.label.color,
          fontSize: theme.palette.toolTip.label.fontSize,
          fontWeight: theme.palette.toolTip.label.fontWeight,
          lineHeight: theme.palette.toolTip.label.lineHeight,
        })}
      >
        1 serving of {label} =
      </Typography>
      <ul
        style={{
          margin: '4px 0 0 16px',
          padding: 0,
        }}
      >
        {examples.map((example, i) => (
          <li
            key={i}
            style={{
              // Use references to the theme in a function so MUI can apply them
              color: 'inherit', // Inherit from parent Typography
              fontSize: 'inherit',
              fontWeight: 'inherit',
              lineHeight: 'inherit',
            }}
          >
            {example}
          </li>
        ))}
      </ul>
    </Box>
  );
};

/**
 * The Food Groups Box displays food groups with their changes in servings
 *
 * @param {number} maxWidth - Maximum width of the box in pixels, defaults to 336
 * @param {string} workflowToken - Optional workflow token to fetch data
 * @returns
 */
const FoodGroupsBox = ({ maxWidth = 336, workflowToken: workflowTokenProp }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [showAll, setShowAll] = useState(false);
  const displayLimit = 10;

  const {
    state: { userObject },
  } = useContext(UserContext);

  // Use the provided token first, fall back to the user context
  const contextWorkflowToken = getWorkflowToken(userObject?.workflowShareUrl);
  const workflowToken = workflowTokenProp || contextWorkflowToken;

  const { data, isLoading } = useFoodGroups(workflowToken);

  // Dynamically calculate nameColumn width based on maxWidth
  const nameColumnWidth = useMemo(() => {
    return maxWidth === 456 ? 233 : 113;
  }, [maxWidth]);

  if (isLoading || !data || data.length === 0) {
    return <></>;
  }

  const displayData = showAll ? data : data.slice(0, displayLimit);

  return (
    <Box sx={{ width: '100%', maxWidth: `${maxWidth}px` }}>
      <Box
        className={classes.frame}
        sx={{ overflow: 'hidden', maxWidth: '100%' }}
      >
        <Box sx={{ width: '100%' }}>
          {/* Header Section */}
          <Box className={classes.header}>
            <Typography variant="h3">All Food Groups</Typography>
            <Typography variant="body2">Change in Servings</Typography>
          </Box>

          {/* Food Groups List */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {displayData.map((foodGroup) => (
              <Box
                key={foodGroup.id}
                className={classes.foodGroupRow}
              >
                {/* Column 1: Food Group Name - dynamic width based on maxWidth */}
                <Box
                  sx={{
                    width: `${nameColumnWidth}px`,
                    minWidth: `${nameColumnWidth}px`,
                  }}
                >
                  <Typography variant="body2">{foodGroup.name}</Typography>
                </Box>

                {/* Column 2: Current → Goal Values */}
                <Box className={classes.valuesColumn}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: '600',
                      color: '#7C7C7C',
                    }}
                  >
                    {foodGroup.currentValue} → {foodGroup.idealValue}
                  </Typography>
                </Box>

                {/* Column 3: Chip */}
                <Box className={classes.chipColumn}>
                  <Chip
                    label={`${foodGroup.change > 0 ? '+' : ''}${foodGroup.change}`}
                    size="small"
                    sx={{
                      minWidth: '60px',
                      ...(foodGroup.change > 0
                        ? theme.globalClasses.chipSuccess
                        : foodGroup.change < 0
                        ? theme.globalClasses.chipError
                        : theme.globalClasses.chipDefault),
                    }}
                  />
                </Box>

                {/* Column 4: Info Icon */}
                <Box className={classes.infoIconColumn}>
                  <Tooltip
                    title={getServingTooltipContent(foodGroup.id, foodGroup.name)}
                    placement="top"
                    arrow
                  >
                    <InfoRounded
                      sx={{
                        color: theme.palette.toolTip?.active || 'rgba(0, 0, 0, 0.54)',
                        width: 24,
                        height: 24,
                      }}
                      fontSize="small"
                    />
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>

          {/* View More Button */}
          {data.length > displayLimit && (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: '16px' }}>
              <Button
                variant="text"
                onClick={() => setShowAll(!showAll)}
                sx={{
                  textTransform: 'none',
                  fontSize: '15px',
                  fontWeight: 700,
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
                endIcon={showAll ? <ExpandLess /> : <ExpandMore />}
              >
                {showAll ? 'VIEW LESS' : 'VIEW MORE'}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FoodGroupsBox;
